import React, { useContext, useState } from "react";
import { signOut } from "firebase/auth";
import { Modal, Box, Typography } from "@mui/material";
import { tracedCallable } from "../utilities/tracedCallable";
import { styled } from "@mui/material/styles";
import { ConfirmAction } from "../assets/controls";
import { useAuth } from "../services/use-auth";
import UserContext from "../assets/user_context";
import { useThemeMode } from "../theme/ThemeContext";
import LoadingLogo from "../assets/logos/animated-logo.svg?react";
import LoadingLogoLight from "../assets/logos/animated-logo-light.svg?react";

// Styled components for logout spinner modal
const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "400px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  textAlign: "center",
}));

const LogoutSpinnerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const SpinnerLogo = styled(Box)(({ theme }) => ({
  width: "80px",
  height: "80px",
  "& svg": {
    width: "100%",
    height: "100%",
  },
}));

const SignOutModal = (props) => {
  const authHook = useAuth();
  const { org, userData } = useContext(UserContext);
  const { mode } = useThemeMode();
  const [showLogoutSpinner, setShowLogoutSpinner] = useState(false);

  const signOutFunc = async () => {
    try {
      // Show logout spinner immediately
      setShowLogoutSpinner(true);

      // Track logout event before signing out
      if (authHook.functions && org) {
        try {
          const trackAuthEvent = tracedCallable(
            authHook.functions,
            "trackAuthEvent",
          );
          await trackAuthEvent({
            action: "logout",
            orgId: org,
            metadata: {
              userAgent: navigator.userAgent,
              platform: navigator.platform,
              timestamp: new Date().toISOString(),
            },
          });
        } catch (trackError) {
          // Don't block logout if tracking fails
        }
      }

      // Set a flag so the next login knows to use default org, not URL org
      sessionStorage.setItem("justLoggedOut", "true");

      await signOut(authHook.auth);

      // Clear the ?org= URL parameter so next login uses default org
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("org")) {
        urlParams.delete("org");
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }

      // Hide spinner and close modal
      setShowLogoutSpinner(false);
      props.handleClose();
    } catch (error) {
      console.error("Error signing out:", error);
      // Hide spinner even on error
      setShowLogoutSpinner(false);
      props.handleClose();
    }
  };

  return (
    <React.Fragment>
      {/* Logout spinner modal */}
      <Modal
        open={showLogoutSpinner}
        onClose={() => {}} // Prevent closing
        disableEscapeKeyDown>
        <ModalBox>
          <LogoutSpinnerContainer>
            <SpinnerLogo>
              {mode === "dark" ? <LoadingLogoLight /> : <LoadingLogo />}
            </SpinnerLogo>
            <Typography variant="h6">Logging You Out...</Typography>
          </LogoutSpinnerContainer>
        </ModalBox>
      </Modal>

      {/* Original confirmation modal */}
      {userData?.username && (
        <ConfirmAction
          dataForAction={{
            label: userData.username,
            actionText: "Sign Out",
          }}
          callback={(data) => signOutFunc(data)}
          openState={props.open}
          handleCloseState={props.handleClose}
          useSlider={true}>
          {`Sign Out as ${userData.username}`}
        </ConfirmAction>
      )}
    </React.Fragment>
  );
};

export default SignOutModal;
